import React, { useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import {
  useRouteError,
  isRouteErrorResponse,
  useNavigateTo,
} from 'infrastructure/routing'
import * as Sentry from '@sentry/react'

/**
 * Route-level error fallback displayed in the content area when a route component throws.
 * Keeps AppLayout (nav, drawer) visible so the user can navigate away.
 */
export function RouteErrorFallback() {
  const error = useRouteError()
  const navigate = useNavigateTo()
  const [eventId, setEventId] = useState<string | undefined>(undefined)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (error && !isRouteErrorResponse(error)) {
      setEventId(Sentry.captureException(error))
      return
    }
    setEventId(undefined)
  }, [error])

  const { title, message } = getErrorDetails(error)
  const isAccessDenied = title === 'Access Denied'

  const errorText = [
    `${title}: ${message}`,
    error instanceof Error && error.stack ? `\n${error.stack}` : '',
    eventId ? `\nError ID: ${eventId}` : '',
  ].join('')

  const handleCopy = async () => {
    await navigator.clipboard.writeText(errorText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Box sx={{ p: 3, maxWidth: 600 }}>
      <Alert severity="error">
        <AlertTitle>{title}</AlertTitle>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {message}
        </Typography>
        {error instanceof Error && error.stack && !isAccessDenied && (
          <Box sx={{ position: 'relative', mt: 1 }}>
            <Tooltip title={copied ? 'Copied!' : 'Copy error details'}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'grey.200',
                  '&:hover': { bgcolor: 'grey.300' },
                }}
              >
                {copied ? (
                  <CheckIcon fontSize="small" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <Box
              component="pre"
              sx={{
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200,
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
              }}
            >
              {error.stack}
            </Box>
          </Box>
        )}
        {eventId && (
          <Typography variant="caption" color="text.secondary">
            Error ID: {eventId}
          </Typography>
        )}
      </Alert>
      <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
        <Button variant="contained" onClick={() => navigate('/')}>
          Go to Dashboard
        </Button>
        <Button variant="outlined" onClick={() => window.location.reload()}>
          Reload Page
        </Button>
      </Stack>
    </Box>
  )
}

const accessDeniedDetails = {
  title: 'Access Denied',
  message:
    'You may be logged in as the wrong user (e.g., a demo account). Please log out of the Kiddom web app and log back in with your admin account, then reload this page.',
}

function getErrorDetails(error: unknown): { title: string; message: string } {
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      return accessDeniedDetails
    }
    if (error.status === 404) {
      return {
        title: 'Page Not Found',
        message: 'The requested page does not exist.',
      }
    }
    return {
      title: `Error ${error.status}`,
      message: error.statusText || 'An unexpected error occurred.',
    }
  }
  if (error instanceof Error) {
    if (/\b403\b/.test(error.message)) {
      return accessDeniedDetails
    }
    return {
      title: 'Something went wrong',
      message: error.message,
    }
  }
  return {
    title: 'Something went wrong',
    message: 'An unexpected error occurred.',
  }
}
